import React, { useCallback, useMemo, useState } from 'react';
import {
  LayoutChangeEvent,
  StyleSheet,
  useWindowDimensions,
  ViewProps,
  ViewStyle,
  Platform,
} from 'react-native';
import format from 'date-fns/format';
import {
  DataProvider,
  LayoutProvider,
  RecyclerListView,
  RecyclerListViewProps,
} from 'recyclerlistview';
import { Attendee, MeetingAttendeeStatus, AttendeeType } from '@alucio/aws-beacon-amplify/src/models';
import {
  DNABox,
  DNAButton,
  DNADivider,
  DNAText,
  GridList,
  Iffy,
  luxColors,
  util,
} from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, SortableGridListHeaderText } from 'src/components/DNA';
import { MeetingORM } from 'src/types/types';
import { useDispatch } from 'src/state/redux';
import meetingQuery from 'src/state/redux/meeting/query';
import { sortCollection } from 'src/state/redux/selector/common';
import { DeviceMode, useAppSettings } from 'src/state/context/AppSettings';
import { drawerActions, DRAWER_ENTITIES } from 'src/state/redux/slice/drawer';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import { CRMAvailabilityStatus } from 'src/state/machines/CRM/crmMachineTypes';
import ContentPresentedList from './ContentPresentedList';
import MeetingContextMenu from './MeetingContextMenu';
import { useIsMeetingLocked } from './AddMeetingProvider';
import CrmStatusCell from './CrmStatusCell';
import CrmIdCell from './CrmIdCell';

const S = StyleSheet.create({
  Col: {
    paddingVertical: 12,
    alignItems: 'flex-start',
  },
  HeaderColTablet: {
    backgroundColor: colors['color-gray-10'],
  },
  PaddedContentRow: {
    paddingVertical: 12,
  },
  SortableButton: {
    minHeight: 17,
    maxHeight: 17,
    right: 8,
  },
  Row: {
    flexBasis: 'auto',
  },
  MobileHeader: {
    backgroundColor: colors['color-ui2-neutral-50'],
    minHeight: 49,
    height: 49,
  },
  attendeeListToggle: {
    right: 8,
  },
  contextMenu: {
    marginLeft: 0,
  },
  tableContainer: {
    overflow: Platform.select({
      web: 'auto',
      default: 'scroll',
    }) as 'scroll', // small hack to fix the overflow issue on web,
  },
  gridListContainer: {
    alignItems: 'flex-start',
    paddingHorizontal: 16,
  },
  desktopCardContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    overflow: 'hidden',
    borderRadius: 4,
    flex: 1,
  },
  rowContainer: {
    width: '100%',
  },
});

const GRID_COL_GAP = '12px'
const GRID_COL_GAP_NUM = 12

interface MeetingHistoryListRowProps {
  meetingORM: MeetingORM,
  isCRMMeetingEnabled?: boolean,
}

const AttendeesList: React.FC<{ attendees: Attendee[] }> = ({ attendees }) => {
  const [showAll, setShowAll] = useState<boolean>(false);
  const activePrimaryAttendee = useMemo(
    () => attendees.find(({ status, attendeeType }) =>
      status !== MeetingAttendeeStatus.REMOVED && attendeeType === AttendeeType.PRIMARY),
    [attendees],
  )
  const activeSecondaryAttendees = useMemo(
    () => attendees
      .filter(({ status, attendeeType }) =>
        status !== MeetingAttendeeStatus.REMOVED && attendeeType === AttendeeType.SECONDARY,
      )
      .sort((a, b) => a.name.localeCompare(b.name)),
    [attendees],
  )

  const toggleAttendeeList = useCallback(
    () => { setShowAll(p => !p) },
    [setShowAll],
  )

  return (
    <DNABox appearance="col" spacing="sm" fill>
      <Iffy is={activePrimaryAttendee}>
        <DNAText numberOfLines={1}>
          {activePrimaryAttendee?.name}
        </DNAText>
      </Iffy>
      {
        activeSecondaryAttendees
          .map((attendee) => (
            <DNAText numberOfLines={1} key={attendee.id}>
              {attendee.name}
            </DNAText>
          ))
          .filter((_, idx) => showAll || idx < (activePrimaryAttendee ? 4 : 5))
      }
      <Iffy is={activeSecondaryAttendees.length > (activePrimaryAttendee ? 4 : 5)}>
        <DNAButton
          appearance="ghost"
          status="primary"
          onPress={toggleAttendeeList}
          style={S.attendeeListToggle}
          children={showAll ? 'Show less' : 'Show all'}
        />
      </Iffy>
    </DNABox>
  );
};

const MeetingHistoryListRow: React.FC<MeetingHistoryListRowProps> = ({ meetingORM, isCRMMeetingEnabled }) => {
  const { startTime, endTime, title, contentPresented, attendees, id, status } = meetingORM.model
  const { deviceMode } = useAppSettings()
  const isTablet = deviceMode === 'tablet'
  const dispatch = useDispatch()
  const isMeetingLocked = useIsMeetingLocked(meetingORM)

  const toggleDrawer = useCallback(
    () => {
      dispatch(drawerActions.toggle({
        entity: DRAWER_ENTITIES.MEETING,
        entityId: meetingORM.model.id,
      }));
    },
    [dispatch],
  )

  const contextMenuStyle = useMemo(() => util.mergeStyles(
    undefined,
    S.Col,
    [S.contextMenu, isTablet],
  ), [isTablet])

  const contextMenu = (
    <GridList.Col
      style={contextMenuStyle}
      span={1}
    >
      <MeetingContextMenu
        isCRMMeetingEnabled={isCRMMeetingEnabled}
        meetingORM={meetingORM}
        isTablet={isTablet}
      />
    </GridList.Col>
  )

  return (
    <GridList.Row
      style={S.Row}
      interactive="hoverable"
      hoverColor={luxColors.mainContainer.primary}
      onPress={toggleDrawer}
      testID="meeting-list-row"
    >
      {/* TABLET CONTEXT MENU */}
      {(isTablet || isCRMMeetingEnabled) && contextMenu}
      {/* CRM SYNC STATUS */}
      {isCRMMeetingEnabled &&
        <GridList.Col style={S.Col} span={1}>
          <CrmStatusCell meetingORM={meetingORM} />
        </GridList.Col>}
      {/* ATTENDEES */}
      <GridList.Col style={S.Col} span={1}>
        <AttendeesList attendees={attendees} />
      </GridList.Col>
      {/* CRM STATUS */}
      {isCRMMeetingEnabled &&
        <GridList.Col style={S.Col} span={1}>
          <DNABox appearance="col" spacing="sm" fill alignX="start">
            <DNAChip.Beacon
              testID="file-chip-badge"
              variant={status === 'PLANNED'
                ? 'MEETING_STATUS_PLANNED'
                : status === 'LOCKED'
                  ? 'MEETING_STATUS_LOCKED'
                  : 'MEETING_STATUS_COMPLETED'
              }
            />
          </DNABox>
        </GridList.Col>}
      {/* CRM INTERACTION NAME */}
      {isCRMMeetingEnabled &&
        <GridList.Col style={S.Col} span={1}>
          <CrmIdCell meetingORM={meetingORM} />
        </GridList.Col>}
      {/* START TIME */}
      <GridList.Col style={S.Col} span={1}>
        <DNABox appearance="col">
          <DNAText>{format(new Date(startTime), 'LLL d, y')}</DNAText>
          <DNAText>{format(new Date(startTime), 'p')}</DNAText>
        </DNABox>
      </GridList.Col>
      {/* END TIME */}
      <GridList.Col style={S.Col} span={1}>
        <DNABox appearance="col">
          <DNAText>{endTime ? format(new Date(endTime), 'LLL d, y') : ''}</DNAText>
          <DNAText>{endTime ? format(new Date(endTime), 'p') : ''}</DNAText>
        </DNABox>
      </GridList.Col>
      {/* CONTENT PRESENTED */}
      <GridList.Col style={S.Col} span={1}>
        <DNABox appearance="col" fill>
          <ContentPresentedList
            isEditForm={false}
            meetingId={id}
            contentPresented={contentPresented || []}
          />
        </DNABox>
      </GridList.Col>
      {/* TITLE */}
      <GridList.Col style={S.Col} span={1}>
        <DNABox appearance="col" spacing="sm" fill>
          <DNAText testID="meeting-title-row" numberOfLines={2}>{title}</DNAText>
          <Iffy is={isMeetingLocked}>
            <DNABox shrink>
              <DNAChip.Beacon children="MEETING IS LOCKED" />
            </DNABox>
          </Iffy>
        </DNABox>
      </GridList.Col>
      {/* DESKTOP CONTEXT MENU */}
      {!isTablet && !isCRMMeetingEnabled && contextMenu}
    </GridList.Row>
  )
}

const MemoizedMeetingHistoryListRow = React.memo(MeetingHistoryListRow, (prevProps, nextProps) => {
  return prevProps.meetingORM === nextProps.meetingORM &&
    prevProps.isCRMMeetingEnabled === nextProps.isCRMMeetingEnabled;
});

const MeetingContent: React.FC<MeetingTableProps> = ({ meetingORM }) => {
  const [isSortedAsc, setIsSortedAsc] = useState<boolean>(false);
  const sortedData = useMemo(
    () => {
      return isSortedAsc
        ? sortCollection(meetingORM, meetingQuery.sorts.startTimeAsc)
        : meetingORM;
    },
    [isSortedAsc, meetingORM],
  )

  const { deviceMode } = useAppSettings()
  const { availabilityStatus } = useCRMStatus();
  const dimensions = useWindowDimensions();
  const isCRMMeetingEnabled = availabilityStatus === CRMAvailabilityStatus.ENABLED
  const {
    headers,
    columnConfig,
    columnConfigMinWidth,
    headersCRM,
    columnConfigCRM,
    columnConfigCRMMinWidth,
  } = deviceModeVariants[deviceMode]
  const [headerWidth, setHeaderWidth] = useState<number>(isCRMMeetingEnabled
    ? columnConfigCRMMinWidth
    : columnConfigMinWidth,
  )

  const isDesktop = deviceMode === 'desktop';
  const enabledHeaders = isCRMMeetingEnabled
    ? (headersCRM || headers)
    : headers

  const enabledColumnConfig = isCRMMeetingEnabled
    ? columnConfigCRM
    : columnConfig

  const dataProviderInit = useMemo(
    () => (new DataProvider((prev: MeetingORM, next: MeetingORM) => prev !== next))
      .cloneWithRows(sortedData),
    [sortedData],
  );

  const measureHeaderWidth = useCallback<NonNullable<ViewProps['onLayout']>>(
    (ev) => {
      const webEvent = ev.nativeEvent as any as LayoutChangeEvent & { target: HTMLElement }
      const scrollWidth = webEvent.target.scrollWidth
      setHeaderWidth(p => p !== scrollWidth ? scrollWidth : p)
    },
    [],
  )

  const layoutProvider = useMemo(
    () => new LayoutProvider(
      () => 'DEFAULT',
      (_, dim) => {
        dim.width = 2000;
        dim.height = 85;
      },
    ),
    [],
  );

  const renderItem = useCallback<RecyclerListViewProps['rowRenderer']>(
    (type, meetingORM: MeetingORM) => {
      return (
        <>
          <DNADivider />
          <MemoizedMeetingHistoryListRow
            key={meetingORM.model.id}
            isCRMMeetingEnabled={isCRMMeetingEnabled}
            meetingORM={meetingORM}
          />
        </>
      );
    },
    [isCRMMeetingEnabled],
  )

  const renderItemWrapper = useCallback<NonNullable<RecyclerListViewProps['renderItemContainer']>>(
    (props, parentProps, children) => {
      const { style } = props as { style: ViewProps };
      return (
        <DNABox {...props} style={{ ...style, width: '100%' }}>
          {children}
        </DNABox>
      );
    },
    [],
  )

  const startTimeSort = useCallback(
    () => {
      setIsSortedAsc((prev) => !prev)
    },
    [setIsSortedAsc],
  )

  const activeStyle = useMemo<Record<string, ViewStyle>>(
    () => ({
      headerContainer: util.mergeStyles(
        undefined,
        [S.MobileHeader, true],
        [{ width: '100%' }, true],
        [{ minWidth: columnConfigMinWidth }, !isCRMMeetingEnabled],
        [{ minWidth: columnConfigCRMMinWidth }, isCRMMeetingEnabled],
      ) as ViewStyle,
      listContainer: {
        flex: 1,
        width: headerWidth,
      },
      headerColPresented: [
        S.PaddedContentRow,
        !isDesktop && S.HeaderColTablet,
      ] as ViewStyle,
      headerCol: [
        S.Col,
        !isDesktop && S.HeaderColTablet,
      ] as ViewStyle,
    }),
    [
      isDesktop,
      headerWidth,
      isCRMMeetingEnabled,
      columnConfigMinWidth,
      columnConfigCRMMinWidth,
    ],
  )

  return (
    <DNABox
      fill
      style={S.tableContainer}
    >
      <GridList
        cols={enabledColumnConfig}
        gap={GRID_COL_GAP}
        style={S.gridListContainer}
      >
        <GridList.Header
          onLayout={measureHeaderWidth}
          style={activeStyle.headerContainer}
        >
          {
            enabledHeaders.map((header) => {
              const isSortable = header === 'Start Time';

              return (
                <GridList.Col
                  key={header}
                  style={
                    header === 'Content Presented'
                      ? activeStyle.headerColPresented
                      : activeStyle.headerCol
                  }
                  span={1}
                >
                  {
                    isSortable
                      ? (
                        <SortableGridListHeaderText
                          lable={header}
                          isSortedAsc={isSortedAsc}
                          onPress={startTimeSort}
                          isActive={true}
                        />
                      )
                      : <DNAText b2 bold>{header}</DNAText>
                  }
                </GridList.Col>
              )
            })
          }
        </GridList.Header>
        {/* Meeting Rows */}
        {
          headerWidth
            ? (
              <RecyclerListView
                style={activeStyle.listContainer}
                layoutProvider={layoutProvider}
                renderAheadOffset={dimensions.height}
                dataProvider={dataProviderInit}
                rowRenderer={renderItem}
                renderItemContainer={renderItemWrapper}
                canChangeSize
                forceNonDeterministicRendering
              />
            )
            : <></>
        }
      </GridList>
    </DNABox>
  )
}

interface MeetingTableProps {
  meetingORM: MeetingORM[],
}

interface DeviceModeVariant {
  component: React.ElementType,
  headers: string[],
  columnConfig: string
  columnConfigMinWidth: number,
  headersCRM?: string[],
  columnConfigCRM?: string,
  columnConfigCRMMinWidth: number,
}

type DeviceModeVariants = Record<DeviceMode, DeviceModeVariant>
const deviceModeVariants: DeviceModeVariants = {
  desktop: {
    component: MeetingContent,
    headers: ['Attendees', 'Start Time', 'End Time', 'Content Presented', 'Title', ''],
    headersCRM: ['', 'CRM Status', 'Attendees', 'Status', 'CRM Interaction Name', 'Start Time', 'End Time',
      'Content Presented', 'Title'],
    columnConfig: [
      'minmax(180px,100%)',
      'minmax(150px,100%)',
      'minmax(150px,100%)',
      'minmax(300px,100%)',
      'minmax(280px,100%)',
      '50px',
    ].join(' '),
    columnConfigMinWidth: (280 + 150 + 150 + 180 + 300 + 50 + (6 * GRID_COL_GAP_NUM)),
    columnConfigCRM: [
      '50px',
      '120px',
      'minmax(180px,100%)',
      '90px',
      '120px',
      'minmax(150px,100%)',
      'minmax(150px,100%)',
      'minmax(300px,100%)',
      'minmax(280px,100%)',
    ].join(' '),
    columnConfigCRMMinWidth: (120 + 180 + 90 + 120 + 150 + 150 + 300 + 280 + 50 + (9 * GRID_COL_GAP_NUM)),
  },
  tablet: {
    component: MeetingContent,
    headers: ['', 'Attendees', 'Start Time', 'End Time', 'Content Presented', 'Title'],
    headersCRM: ['', 'CRM Status', 'Attendees', 'Status', 'CRM Interaction Name', 'Start Time', 'End Time',
      'Content Presented', 'Title'],
    columnConfig: '40px minmax(180px,500px) 150px 150px minmax(300px,700px) minmax(200px,500px)',
    columnConfigMinWidth: (40 + 200 + 150 + 150 + 180 + 300 + (6 * GRID_COL_GAP_NUM)),
    columnConfigCRM: '40px 140px minmax(180px,500px) 110px 120px 150px 150px minmax(300px,700px) minmax(200px,500px)',
    columnConfigCRMMinWidth: (40 + 140 + 180 + 110 + 120 + 150 + 150 + 300 + 200 + (9 * GRID_COL_GAP_NUM)),
  },
}

const MeetingTable: React.FC<MeetingTableProps> = (props) => {
  const { deviceMode } = useAppSettings()
  const CurrentDeviceModeVariant = deviceModeVariants[deviceMode].component
  return <CurrentDeviceModeVariant {...props} />
}

export default MeetingTable;
